"use client";

import Breadcrumbs from "../layout/Breadcrumbs";
import Accordion, { AccordionItem } from "../ui/Accordion";
import { calculatorNameMap } from "@/lib/constants/navigation";
import { categoryCalculators } from "@/lib/constants/categories";
import { getCalculatorConfig } from "@/lib/constants/calculators";
import { comparisonsMap } from "@/data/comparisons";
import { getGlossaryTermsForCalculator } from "@/lib/glossary/related-terms";
import RelatedTools from "../blog/RelatedTools";
import YouMightAlsoLike from "../blog/YouMightAlsoLike";
import type { YouMightAlsoLikeItem } from "@/lib/blog/ymal-mapper";
import SourcesAndMethodology from "../layout/SourcesAndMethodology";
import EeatTrustBadge from "../layout/EeatTrustBadge";
import Link from "next/link";

import { Calendar, BookOpen, AlertTriangle, ArrowLeft } from "lucide-react";
import { SITE_AUTHOR } from "@/lib/constants/eeat";

interface CalculatorLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  countryCode: string;
  category: string;
  educationalContent?: React.ReactNode;
  faqs?: AccordionItem[];
  // E-E-A-T properties
  lastUpdated?: string;
  sources?: string[];
  methodology?: string[];
  limitations?: string[];
  authorName?: string;
  authorCredentials?: string;
  calculatorSlug?: string;
  relatedTools?: string[];
  // Related blog posts computed server-side
  relatedBlogPosts?: Array<{ title: string; slug: string; summary: string; images?: Array<{ url: string; alt: string }> }>;
  // "You Might Also Like" items computed server-side (the mapper's data pool
  // imports the full posts module, which must never enter a client bundle)
  ymalItems?: YouMightAlsoLikeItem[];
}

function getTextFromReactNode(node: any): string {
  if (node === null || node === undefined) {
    return "";
  }
  if (typeof node === "string" || typeof node === "number" || typeof node === "boolean") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromReactNode).join("");
  }
  if (typeof node === "object" && node.props && node.props.children !== undefined) {
    return getTextFromReactNode(node.props.children);
  }
  return "";
}

export default function CalculatorLayout({
  children,
  title,
  description,
  countryCode,
  category,
  educationalContent,
  faqs,
  lastUpdated,
  sources,
  methodology,
  limitations,
  authorName,
  authorCredentials,
  calculatorSlug,
  relatedTools,
  relatedBlogPosts = [],
  ymalItems = [],
}: CalculatorLayoutProps) {
  const lowercaseCategory = category.toLowerCase();
  const lowercaseCountry = countryCode.toLowerCase();
  
  // Resolve related links dynamically (calculators and comparisons cross-referencing)
  const resolvedRelated: Array<{ title: string; href: string; description: string; type: "calculator" | "comparison" | "glossary" | "guide" }> = [];
  const addedHrefs = new Set<string>(); // Track added hrefs to prevent duplicates
  
  if (relatedTools && relatedTools.length > 0) {
    relatedTools.forEach((slug) => {
      const calcConf = getCalculatorConfig(slug);
      if (calcConf) {
        const isSupportedForCountry = calcConf.countries === "all" || calcConf.countries.includes(lowercaseCountry);
        if (!isSupportedForCountry) return;

        const href = `/${lowercaseCountry}/${calcConf.category}/${slug}`;
        if (addedHrefs.has(href)) return; // Skip if already added
        
        resolvedRelated.push({
          title: calcConf.name,
          href,
          // Registry descriptions are mandatory (validate:content gates empties) — no template fallback.
          description: calcConf.description,
          type: "calculator"
        });
        addedHrefs.add(href);
      } else {
        const compConf = comparisonsMap[slug];
        if (compConf) {
          const href = `/compare/${slug}`;
          if (addedHrefs.has(href)) return; // Skip if already added
          
          resolvedRelated.push({
            title: compConf.title,
            href,
            description: compConf.description,
            type: "comparison"
          });
          addedHrefs.add(href);
        }
      }
    });
  }

  // Fallback to same-category calculators if none specified
  if (resolvedRelated.length === 0) {
    const relatedSlugs = (categoryCalculators[lowercaseCategory] || [])
      .filter((slug) => !title.toLowerCase().includes(slug.replace("-", " ")))
      .slice(0, 3);

    relatedSlugs.forEach((slug) => {
      const name = calculatorNameMap[slug] || slug;
      const calcConf = getCalculatorConfig(slug);
      const categoryPath = calcConf ? calcConf.category : lowercaseCategory;
      const description = calcConf?.description;
      if (!description) return; // no registry description → skip the entry, never template one
      
      const href = `/${lowercaseCountry}/${categoryPath}/${slug}`;
      if (addedHrefs.has(href)) return; // Skip if already added
      
      resolvedRelated.push({
        title: name,
        href,
        description,
        type: "calculator"
      });
      addedHrefs.add(href);
    });
  }

  // Link to the first related country post if available in relatedBlogPosts
  const countryPost = relatedBlogPosts && relatedBlogPosts.length > 0 ? relatedBlogPosts[0] : null;
  if (countryPost) {
    const postHref = `/blog/${countryPost.slug}`;
    if (!addedHrefs.has(postHref)) {
      resolvedRelated.push({
        title: countryPost.title,
        href: postHref,
        description: countryPost.summary,
        type: "guide"
      });
      addedHrefs.add(postHref);
    }
  }

  // Add individual glossary term links related to this calculator
  if (calculatorSlug) {
    const relatedGlossaryTerms = getGlossaryTermsForCalculator(calculatorSlug, lowercaseCountry);
    for (const gt of relatedGlossaryTerms) {
      const href = `/${gt.country}/glossary/${gt.slug}`;
      if (!addedHrefs.has(href)) {
        resolvedRelated.push({
          title: gt.term,
          href,
          description: gt.definition,
          type: "glossary"
        });
        addedHrefs.add(href);
      }
    }
  }

  // WebApplication (co-typed with SoftwareApplication) — a calculator is an
  // interactive browser tool, so WebApplication is the precise, accurate type,
  // while keeping SoftwareApplication ensures Google's Software-App parser and
  // knowledge-graph entity linking still recognize it (2026 search/AI priority).
  const appSchema = {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "WebApplication"],
    "@id": `https://financelives.com/${lowercaseCountry}/${lowercaseCategory}/${calculatorSlug || title.toLowerCase().replace(/\s+/g, '-')}#softwareapplication`,
    "name": title,
    "description": description,
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "browserRequirements": "Requires HTML5. Requires JavaScript.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  // FAQPage schema
  const faqSchema = faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.title,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": getTextFromReactNode(faq.content),
      },
    })),
  } : null;

  // Breadcrumb schema
  const breadcrumbItems: Record<string, unknown>[] = [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://financelives.com" },
    { "@type": "ListItem", "position": 2, "name": countryCode.toUpperCase(), "item": `https://financelives.com/${lowercaseCountry}` },
    { "@type": "ListItem", "position": 3, "name": category, "item": `https://financelives.com/${lowercaseCountry}/${lowercaseCategory}` },
  ];
  if (calculatorSlug) {
    breadcrumbItems.push({ "@type": "ListItem", "position": 4, "name": title });
  }
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems
  };

  // Person schema for E-E-A-T author attribution
  const authorSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": SITE_AUTHOR.name,
    "jobTitle": SITE_AUTHOR.jobTitle,
    "description": SITE_AUTHOR.shortBio,
    "url": SITE_AUTHOR.url,
    "image": SITE_AUTHOR.image,
    "email": SITE_AUTHOR.email,
    "sameAs": SITE_AUTHOR.sameAs,
  };

  // Article schema for the on-page educational guide (emitted only when present).
  // A calculator page is tool-first, but it also ships a substantial, visible
  // "Guide & How-To" section + FAQ — so, like the compare pages, we co-emit an
  // Article for that editorial layer to earn rich-result eligibility and E-E-A-T.
  // Dates are omitted deliberately: calculators carry a human "July 2026" review
  // label, not a per-page ISO date, and we never fabricate one.
  const guideArticleSchema = educationalContent ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `https://financelives.com/${lowercaseCountry}/${lowercaseCategory}/${calculatorSlug || title.toLowerCase().replace(/\s+/g, '-')}#guide`,
    "headline": title,
    "description": description,
    "image": ["https://financelives.com/images/og/og-image.png"],
    "inLanguage": "en",
    "author": {
      "@type": "Person",
      "name": SITE_AUTHOR.name,
      "url": SITE_AUTHOR.url,
      "image": SITE_AUTHOR.image,
      "jobTitle": SITE_AUTHOR.jobTitle,
      "description": SITE_AUTHOR.shortBio
    },
    "publisher": {
      "@type": "Organization",
      "name": "FinanceLives",
      "url": "https://financelives.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://financelives.com/logo.png",
        "width": 512,
        "height": 512
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://financelives.com/${lowercaseCountry}/${lowercaseCategory}/${calculatorSlug || title.toLowerCase().replace(/\s+/g, '-')}#webpage`
    }
  } : null;

  return (
    <div className="layout-wrapper anim-fade-in">
      {/* Structured Data Markups */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(appSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(authorSchema) }}
      />
      {guideArticleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(guideArticleSchema) }}
        />
      )}

      <div className="container">
        {/* Breadcrumbs */}
        <Breadcrumbs />

        {/* Back link */}
        <div className="back-link-wrapper">
          <Link href={`/${lowercaseCountry}/${lowercaseCategory}`} className="back-link">
            <ArrowLeft size={16} /> Back to {countryCode.toUpperCase()} {category} Calculators
          </Link>
        </div>

        {/* Header Section */}
        <div className="calculator-header">
          <h1 className="calculator-title">{title}</h1>
          <p className="calculator-description">{description}</p>
        </div>

        {/* Main calculator content */}
        <div className="main-grid">
          <div className="calculator-main-content">
            {/* The actual calculator form and interactive results */}
            <section className="calculator-section">
              {children}
            </section>

            {/* Educational content section below the calculator */}
            {educationalContent && (
              <>
                <div className="section-divider"></div>
                <section className="educational-section">
                  <h2 className="section-title">Guide &amp; How-To</h2>
                  <div className="educational-content">
                    {educationalContent}
                  </div>
                </section>
              </>
            )}

            {/* E-E-A-T Trust Badge */}
            <div className="section-divider"></div>
            <EeatTrustBadge
              label="Built & Maintained"
              authorName={authorName}
              authorCredentials={authorCredentials}
              lastUpdated={lastUpdated}
              style={{ marginTop: 0, marginBottom: 0 }}
            />

            {/* FAQ section */}
            {faqs && faqs.length > 0 && (
              <>
                <div className="section-divider"></div>
                <section className="faq-section">
                  <h2 className="section-title">Frequently Asked Questions</h2>
                  <Accordion items={faqs} />
                </section>
              </>
            )}

          </div>
        </div>

        {/* Related Tools & Comparisons using RelatedTools Component */}
        {resolvedRelated.length > 0 && (
          <>
            <div className="section-divider"></div>
            <div>
              <RelatedTools tools={resolvedRelated} postCountryCode={lowercaseCountry} compact={false} />
            </div>
          </>
        )}

        {/* You Might Also Like — blog posts related to this calculator */}
        {ymalItems.length > 0 && (
          <>
            <div className="section-divider"></div>
            <YouMightAlsoLike noMargin={true} items={ymalItems} />
          </>
        )}

        {/* E-E-A-T: primary-source citations + editorial process */}
        <div className="section-divider"></div>
        <div className="sources-block-wrapper" style={{ marginTop: 0 }}>
          <SourcesAndMethodology country={lowercaseCountry} category={lowercaseCategory} lastUpdated={lastUpdated} compact={true} />
        </div>

        {/* Calculator Disclaimer - Slanted Design at End */}
        <div className="section-divider"></div>
        <footer className="slanted-disclaimer">
          Disclaimer: All calculations are estimates based on current statutory data and user inputs. Tax rates, retirement regulations, contribution limits, deduction thresholds, and investment fees change over time and vary by jurisdiction. This calculator does not constitute financial, investment, tax, or legal advice. Always verify critical values with an official professional advisor or reference the official government publications cited above before making any financial decisions.
        </footer>

      </div>

      <style jsx>{`
        .layout-wrapper {
          padding-top: var(--space-4);
          padding-bottom: var(--space-12);
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }

        .calculator-header {
          margin-bottom: 0;
          width: 100%;
        }
        
        .calculator-title {
          font-family: var(--font-heading);
          font-size: var(--text-3xl);
          font-weight: var(--fw-extrabold);
          margin-bottom: var(--space-3);
          color: var(--text-primary);
          line-height: var(--lh-tight);
          letter-spacing: var(--tracking-tight);
          word-wrap: break-word;
        }
        
        .calculator-description {
          font-size: var(--text-md);
          line-height: var(--lh-relaxed);
          color: var(--text-secondary);
          max-width: 100%;
          margin-bottom: var(--space-3);
          word-wrap: break-word;
        }
        
        .main-grid {
          display: block;
          width: 100%;
          max-width: 100%;
        }
        
        .calculator-main-content {
          display: flex;
          flex-direction: column;
          gap: 0;
          min-width: 0;
          width: 100%;
          max-width: 100%;
          margin-top: var(--space-4);
        }
        
        .calculator-section,
        .educational-section,
        .faq-section {
          width: 100%;
          max-width: 100%;
          margin-top: 0;
          margin-bottom: 0;
        }

        .educational-content > *:last-child,
        .educational-content :global(*:last-child),
        .educational-content :global(div:last-child) {
          margin-bottom: 0 !important;
        }

        .section-title {
          font-family: var(--font-heading);
          font-size: var(--text-2xl);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin-bottom: var(--space-5);
          padding-left: var(--space-3);
          border-left: 4px solid var(--primary);
          line-height: var(--lh-snug);
        }
        
        .educational-content {
          line-height: 1.7;
          color: var(--text-secondary);
          width: 100%;
        }
        
        .educational-content :global(p) {
          margin-bottom: var(--space-4);
          font-size: var(--text-base);
          line-height: var(--lh-loose);
        }
        
        .educational-content :global(h3) {
          font-family: var(--font-heading);
          font-size: var(--text-xl);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin-top: var(--space-6);
          margin-bottom: var(--space-3);
          line-height: var(--lh-snug);
        }
        
        .educational-content :global(ul), 
        .educational-content :global(ol) {
          margin-bottom: var(--space-4);
          padding-left: var(--space-6);
        }
        
        .educational-content :global(li) {
          margin-bottom: var(--space-2);
          line-height: 1.6;
        }
        
        .educational-content :global(code) {
          background-color: var(--bg-elevated);
          padding: 0.125rem 0.25rem;
          border-radius: var(--radius-sm);
          font-family: monospace;
          font-size: 0.9em;
        }
        
        .sources-block-wrapper section {
          margin-top: 0 !important;
        }
        .faq-section {
          margin-top: 0;
          margin-bottom: 0;
        }
        
        .slanted-disclaimer {
          padding: 0;
          margin: 0;
          font-size: var(--text-2xs);
          color: var(--text-muted, var(--text-secondary));
          line-height: 1.5;
          font-style: italic;
          background: transparent;
          border: none;
        }
      `}</style>
    </div>
  );
}
